import { IconButton, merge, MessageBar, MessageBarType, ScrollablePane, Stack, StackItem, Text } from '@fluentui/react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMessages } from '../../../contexts/messageContext';
import { ResponsiveSize, useResponsiveSize } from '../../../contexts/responsiveContext';
import Page from '../../Page';
import Inputs from './Inputs';
import Message from './Message';

const Messages = () => {

    const { messages } = useMessages();
    const { responsiveSize } = useResponsiveSize();

    const messagesListRef = useRef();

    const [cursor, setCursor] = useState(undefined);
    const [searchedValue, setSearchedValue] = useState(undefined);

    useEffect(() => {
        messagesListRef?.current?.scrollIntoView();
    }, [messages, responsiveSize]);

    const styles = useMemo(
        () => {
            let styles = {
                messages: { root: { position: "relative" } },
                scrollablePane: {
                    contentContainer: {
                        background: "white",
                        borderTop: "1px solid #F0F0F0",
                        marginLeft: -34, // Prolonge la bordure supérieure
                        marginRight: -34, // Colle la scrollbar au côté droit de l'écran
                        paddingBottom: 13,
                        paddingLeft: 34,
                        paddingRight: 34,
                        paddingTop: 13,
                    }
                },
                text: {
                    root: {
                        fontSize: 13,
                        lineHeight: "16px"
                    }
                }
            };

            if (responsiveSize >= ResponsiveSize.medium)
                styles = merge(
                    styles,
                    {
                        scrollablePane: {
                            contentContainer: {
                                //background: "unset",
                                //marginLeft: "unset",
                                //marginRight: "unset",
                                marginLeft: 1, // Décalage pour afficher la ligne verticale du fond
                                marginRight: 1, // Décalage pour afficher la ligne verticale du fond
                                paddingLeft: "unset",
                                paddingRight: "unset",
                            }
                        }
                    }
                );

            return styles;
        },
        [responsiveSize]
    );

    const onSearch = useCallback((newValue) => {
        setSearchedValue(newValue);
        setCursor(undefined);
    }, []);

    const resultsCount = useMemo(() => {
        if (!searchedValue || searchedValue.length <= 2) return null;
        else {
            let count = 0;
            const regex = new RegExp(`(${searchedValue})`, "gi");
            messages.forEach(message => {
                const { piecesJointes, texte, texteMessageParent } = message;

                if (!!texte) {
                    const matches = texte.match(regex) || [];
                    count += matches.length;
                }

                if (!!texteMessageParent) {
                    const matches = texteMessageParent.match(regex) || [];
                    count += matches.length;
                }

                piecesJointes.forEach(pieceJointe => {
                    const { nom } = pieceJointe;
                    const matches = nom.match(regex) || [];
                    count += matches.length;
                });
            });
            return count;
        }
    }, [messages, searchedValue]);

    const onClickCaret = useCallback((offset) => () => {
        const highlighted = document.querySelectorAll("[data-highlighted='true']");
        setCursor(prev => {
            let newIndex = prev;
            if (isNaN(newIndex)) newIndex = 0;
            else {
                newIndex = prev + offset;
                if (newIndex < 0) newIndex = highlighted.length - 1;
                if (newIndex >= highlighted.length) newIndex = 0;
            }
            highlighted[newIndex].scrollIntoView({ block: "center" });
            return newIndex;
        });
    }, []);

    return <Page title="Messages" onSearch={onSearch} searchedValue={searchedValue}>

        {!!searchedValue && searchedValue.length > 2 &&
            <MessageBar
                actions={
                    <>
                        <IconButton disabled={!resultsCount} iconProps={{ iconName: "CaretSolidUp" }} onClick={onClickCaret(-1)} />
                        <IconButton disabled={!resultsCount} iconProps={{ iconName: "CaretSolidDown" }} onClick={onClickCaret(1)} />
                        {!isNaN(cursor) && <Text styles={styles.text}>{`${cursor + 1}/${resultsCount}`}</Text>}
                    </>
                }
                isMultiline={false}
                messageBarType={MessageBarType.info}
                styles={{ content: { alignItems: "center" } }}
            >
                {resultsCount} résultat(s)
            </MessageBar>
        }

        <StackItem grow verticalFill styles={styles.messages}>
            <ScrollablePane styles={styles.scrollablePane}>
                <Stack tokens={{ childrenGap: 26 }}>
                    {Array.isArray(messages) && messages.map((message, index) => {
                        const { dateEnvoi, idMessage, idMessageParent, loginCompte, piecesJointes, texte, texteMessageParent } = message;
                        return <Message
                            dateEnvoi={dateEnvoi}
                            key={index}
                            idMessage={idMessage}
                            idMessageParent={idMessageParent}
                            loginCompte={loginCompte}
                            piecesJointes={piecesJointes}
                            searchedValue={searchedValue}
                            texte={texte}
                            texteMessageParent={texteMessageParent}
                        />;
                    })}
                </Stack>
                <div ref={messagesListRef} />
            </ScrollablePane>
        </StackItem>

        {responsiveSize >= ResponsiveSize.medium && <Inputs />}

    </Page>;

};

export default Messages;